import { HeaderContext } from '@tanstack/vue-table';
import { CustomColumn, ExtendedGridDataType, GridColumnType } from '@/common/utils/types';
import {
  FilterItem,
  FilterSearchOperator,
  FilterSearchSpecificFilterFnKey,
  FilterSelectedValues,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import { isArray, isBoolean, isNumber, isString, isObject, isUndefined, isNil } from 'lodash-es';
import {
  FILTER_SEARCH_OPERATORS_BY_SPECIFIC_FILTER_FN,
  FILTER_SEARCH_TOKEN_VALUE_CONVERTERS_BY_SPECIFIC_FILTER_FN,
} from '@/common/components/molecules/filterSearch/filterSearch.define';
import {
  GridColumnDef,
  GridRawRowDataType,
  GridRowDataType,
} from '@/common/components/molecules/modernGrid/modernGrid.types';
import { getCanFilterColumns } from '@/common/components/molecules/modernGrid/modernGrid.utils';
import { standardTimeToUtcZeroTime } from '@/common/utils/browser/conversion';
import dayjs from 'dayjs';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';

// dayjs에 플러그인 기능.
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

type Visible = boolean;

export const getOperatorsByType = (
  type: ExtendedGridDataType,
  specificFilterFnKey?: FilterSearchSpecificFilterFnKey,
): FilterSearchOperator[] => {
  const columnType = specificFilterFnKey
    ? FILTER_SEARCH_OPERATORS_BY_SPECIFIC_FILTER_FN[specificFilterFnKey]
    : type;

  switch (columnType) {
    case 'float':
    case 'number':
    case 'stringNumber':
    case 'age':
      return ['==', '!=', '>', '>=', '<', '<='];
    case 'boolean':
      return ['==', '!='];
    case 'string':
    default:
      return ['==', '!=', 'like', 'not like'];
  }
};

/**
 * specificFilterFn에 넘기기 전, 컬럼 정의(rendererType/attrs.unit)에 따라
 * 셀 값에 단위를 붙여 필터 함수가 단위 변환 후 비교할 수 있도록 가공한다.
 *
 * - rendererType === 'toSec': `"${rawValue} ${attrs.unit || 'ms'}"` 형태로 반환
 *   (attrs.unit 미설정 시 데이터가 ms 단위임을 가정)
 * - attrs.unit 있음: `"${rawValue} ${attrs.unit}"` 형태로 반환
 * - 그 외 (rendererType 없고 attrs.unit 없음): rawValue 그대로 반환
 *   → 필터 함수가 자신의 default 단위로 해석 (예: toSecByUnit은 's'로 취급)
 */
export const getSpecificFilterCellValue = (
  rawValue: string | number | undefined,
  column?: Pick<CustomColumn, 'rendererType' | 'attrs'>,
): string | number | undefined => {
  if (rawValue === undefined) {
    return rawValue;
  }

  if (column?.rendererType === 'toSec') {
    return `${rawValue} ${column.attrs?.unit || 'ms'}`;
  }

  if (column?.attrs?.unit) {
    return `${rawValue} ${column.attrs.unit}`;
  }

  return rawValue;
};

/**
 * 필터 대상 값을 문자열로 변환한다.
 * ex. [1, 2, 3] → "1,2,3"
 * ex. { a: 1, b: 2 } → "1,2"
 * ex. 1 → "1"
 * @param value
 * @returns
 */
export const getFilterableValue = (value: any): string | number | undefined => {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'object' && v !== null && 'name' in v ? v.name : v))
      .join(',');
  }
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).join(',');
  }
  return value;
};

export const getDefaultUnitBySpecificFilterFnKey = (
  specificFilterFnKey?: FilterSearchSpecificFilterFnKey,
): string => {
  switch (specificFilterFnKey) {
    case 'readableBytes':
    case 'toBytes':
      return 'byte';
    case 'toKiB':
      return 'KiB';
    case 'toMB':
      return 'MB';
    case 'bytesToMiB':
      return 'MiB';
    case 'toMs':
      return 'ms';
    case 'rendererTypeSec':
      return 's';
    case 'toSecByUnit':
      return 's';
    case 'toBps':
      return 'bps';
    case 'mWToW':
      return 'W';
    case 'age':
      return 'day';
    default:
      return '';
  }
};

const stringToNumber = (str: string) => {
  const targetString = str.replace(/,/g, '');
  return parseFloat(targetString);
};

const getNormalizeSpacesString = (str: string) => {
  return str.replace(/\s{2,}/g, ' ');
};

const compareStringValue = (cellValue: string, searchValue: string, isEqual: boolean): boolean => {
  // 문자열 비교
  const stringMatch =
    getNormalizeSpacesString(cellValue).toLowerCase() ===
    getNormalizeSpacesString(searchValue).toLowerCase();

  const trimCellValue = cellValue.trim();
  const trimSearchValue = searchValue.trim();

  // 문자열이 유효한 숫자(정수/실수) 형식인지 검사하는 정규식
  const isNumericRegex = /^-?\d*\.?\d+$/;
  const isCellNumeric = isNumericRegex.test(trimCellValue);
  const isSearchNumeric = isNumericRegex.test(trimSearchValue);

  // 두 값 모두 유효한 숫자
  if (isCellNumeric && isSearchNumeric) {
    const cellNumberValue = Number(trimCellValue);
    const searchNumberValue = Number(trimSearchValue);

    const numberMatch = cellNumberValue === searchNumberValue;
    // 문자열 또는 숫자 중 하나라도 일치하면 참(true)
    return isEqual ? stringMatch || numberMatch : !stringMatch && !numberMatch;
  }

  return isEqual ? stringMatch : !stringMatch;
};

const isEqual = (
  cellValue: unknown,
  searchValues: string[],
  type?: ExtendedGridDataType,
): Visible => {
  if (cellValue !== undefined && (isNumber(cellValue) || type === 'number')) {
    const normalizedCellValue = isString(cellValue) ? stringToNumber(cellValue) : cellValue;
    if (!Number.isNaN(normalizedCellValue)) {
      return searchValues.some((value) => normalizedCellValue === stringToNumber(value));
    }
  }

  if (isBoolean(cellValue)) {
    return searchValues.some((value) => cellValue.toString() === value.toLowerCase());
  }

  if (isString(cellValue)) {
    return searchValues.some((value) => compareStringValue(cellValue, value, true));
  }

  if (isArray(cellValue)) {
    return cellValue?.length ? cellValue?.some((value) => isEqual(value, searchValues)) : false;
  }

  if (isObject(cellValue)) {
    return Object.values(cellValue).some((value) => isEqual(value, searchValues));
  }

  return false;
};

const isNotEqual = (
  cellValue: unknown,
  searchValues: string[],
  type?: ExtendedGridDataType,
): Visible => {
  if (cellValue === null) {
    return true;
  }

  if (cellValue !== undefined && (isNumber(cellValue) || type === 'number')) {
    const normalizedCellValue = isString(cellValue) ? stringToNumber(cellValue) : cellValue;
    if (!Number.isNaN(normalizedCellValue)) {
      return searchValues.every((value) => normalizedCellValue !== stringToNumber(value));
    }
  }

  if (isBoolean(cellValue)) {
    return searchValues.every((value) => cellValue.toString() !== value.toLowerCase());
  }

  if (isString(cellValue)) {
    return searchValues.every((value) => compareStringValue(cellValue, value, false));
  }

  if (isArray(cellValue)) {
    return cellValue?.length ? cellValue?.every((value) => isNotEqual(value, searchValues)) : true;
  }

  if (isObject(cellValue)) {
    return !Object.values(cellValue).some((value) => !isNotEqual(value, searchValues));
  }

  return false;
};

const isLike = (cellValue: unknown, searchValue: string): Visible => {
  if (isString(cellValue)) {
    return getNormalizeSpacesString(cellValue)
      .toLowerCase()
      .includes(getNormalizeSpacesString(searchValue).toLowerCase());
  }

  if (isNumber(cellValue)) {
    return `${cellValue}`.includes(searchValue.toLowerCase());
  }

  if (isBoolean(cellValue)) {
    return `${cellValue}`.toLowerCase().includes(searchValue.toLowerCase());
  }

  if (isArray(cellValue)) {
    return cellValue.length ? cellValue.some((value) => isLike(value, searchValue)) : false;
  }

  if (isObject(cellValue)) {
    return Object.values(cellValue).some((value) => isLike(value, searchValue));
  }

  return false;
};

const isNotLike = (cellValue: unknown, searchValue: string): Visible => {
  if (cellValue === null) {
    return true;
  }

  if (isString(cellValue)) {
    return !getNormalizeSpacesString(cellValue)
      .toLowerCase()
      .includes(getNormalizeSpacesString(searchValue).toLowerCase());
  }

  if (isNumber(cellValue)) {
    return !`${cellValue}`.includes(searchValue.toLowerCase());
  }

  if (isBoolean(cellValue)) {
    return !`${cellValue}`.toLowerCase().includes(searchValue.toLowerCase());
  }

  if (isArray(cellValue)) {
    return cellValue.length ? cellValue.every((value) => isNotLike(value, searchValue)) : true;
  }

  if (isObject(cellValue)) {
    return !Object.values(cellValue).some((value) => !isNotLike(value, searchValue));
  }

  return false;
};

const isGreaterThan = (cellValue: unknown, searchValue: string): Visible => {
  if (isNumber(cellValue)) {
    return cellValue > stringToNumber(searchValue);
  }

  if (isString(cellValue)) {
    return stringToNumber(getNormalizeSpacesString(cellValue)) > stringToNumber(searchValue);
  }

  if (isArray(cellValue)) {
    return cellValue.some((value) => isGreaterThan(value, searchValue));
  }

  if (isObject(cellValue)) {
    return Object.values(cellValue).some((value) => isGreaterThan(value, searchValue));
  }

  return false;
};

const isGreaterThanOrEqual = (cellValue: unknown, searchValue: string): Visible => {
  if (isNumber(cellValue)) {
    return cellValue >= stringToNumber(searchValue);
  }

  if (isString(cellValue)) {
    return stringToNumber(getNormalizeSpacesString(cellValue)) >= stringToNumber(searchValue);
  }

  if (isArray(cellValue)) {
    return cellValue.some((value) => isGreaterThanOrEqual(value, searchValue));
  }

  if (isObject(cellValue)) {
    return Object.values(cellValue).some((value) => isGreaterThanOrEqual(value, searchValue));
  }

  return false;
};

const isLessThan = (cellValue: unknown, searchValue: string): Visible => {
  if (isNumber(cellValue)) {
    return cellValue < stringToNumber(searchValue);
  }

  if (isString(cellValue)) {
    return stringToNumber(getNormalizeSpacesString(cellValue)) < stringToNumber(searchValue);
  }

  if (isArray(cellValue)) {
    return cellValue.some((value) => isLessThan(value, searchValue));
  }

  if (isObject(cellValue)) {
    return Object.values(cellValue).some((value) => isLessThan(value, searchValue));
  }

  return false;
};

const isLessThanOrEqual = (cellValue: unknown, searchValue: string): Visible => {
  if (isNumber(cellValue)) {
    return cellValue <= stringToNumber(searchValue);
  }

  if (isString(cellValue)) {
    return stringToNumber(getNormalizeSpacesString(cellValue)) <= stringToNumber(searchValue);
  }

  if (isArray(cellValue)) {
    return cellValue.some((value) => isLessThanOrEqual(value, searchValue));
  }

  if (isObject(cellValue)) {
    return Object.values(cellValue).some((value) => isLessThanOrEqual(value, searchValue));
  }

  return false;
};

export const compareNumberValues = ({
  cellValue,
  searchValue,
  operator,
}: {
  cellValue: string | number | undefined;
  searchValue: string | number | undefined;
  operator: string;
}): Visible => {
  if (cellValue === undefined || searchValue === undefined) {
    return false;
  }

  let compareA: number;
  if (typeof cellValue === 'string') {
    const convertedValueNumber = stringToNumber(cellValue);
    if (Number.isNaN(convertedValueNumber)) {
      return false;
    }
    compareA = convertedValueNumber;
  } else {
    compareA = cellValue;
  }

  const compareB: number =
    typeof searchValue === 'string' ? stringToNumber(searchValue) : searchValue;

  switch (operator) {
    case '==':
      return compareA === compareB;
    case '!=':
      return compareA !== compareB;
    case '>':
      return compareA > compareB;
    case '>=':
      return compareA >= compareB;
    case '<':
      return compareA < compareB;
    case '<=':
      return compareA <= compareB;
    default:
      return false;
  }
};

export const filterFunctionByOperator = ({
  targetValue,
  operator,
  searchValues,
  type,
}: {
  targetValue: unknown;
  operator: string;
  searchValues: string[];
  type?: ExtendedGridDataType;
}): Visible => {
  switch (operator) {
    case '==':
      return isEqual(targetValue, searchValues, type);
    case '!=':
      return isNotEqual(targetValue, searchValues, type);
    case 'like':
      return isLike(targetValue, searchValues[0]);
    case 'not like':
      return isNotLike(targetValue, searchValues[0]);
    case '>':
      return isGreaterThan(targetValue, searchValues[0]);
    case '>=':
      return isGreaterThanOrEqual(targetValue, searchValues[0]);
    case '<':
      return isLessThan(targetValue, searchValues[0]);
    case '<=':
      return isLessThanOrEqual(targetValue, searchValues[0]);
    default:
      return false;
  }
};

const createFilterItem = ({
  caption,
  field,
  type,
  specificFilterFnKey,
  attrs,
  defaultSearchField,
}: {
  caption: string;
  field: string;
  type: string | undefined;
  specificFilterFnKey?: FilterSearchSpecificFilterFnKey;
  attrs?: {
    unit?: string;
    dataSet?: (string | number)[];
  };
  defaultSearchField?: boolean;
}): FilterItem => {
  if (attrs?.dataSet && isArray(attrs.dataSet)) {
    return {
      key: {
        id: field,
        name: caption,
      },
      operators: ['==', '!='],
      values: {
        multi: true,
        items: attrs.dataSet.map((data: string | number) => ({
          id: `${data}`,
          name: `${data}`,
        })),
      },
      defaultSearchField,
    };
  }

  return {
    key: {
      id: field,
      name: caption,
    },
    operators: getOperatorsByType(type as GridColumnType, specificFilterFnKey),
    values: {
      multi: type === 'boolean',
      items:
        type === 'boolean'
          ? [
              { id: 'true', name: 'True' },
              { id: 'false', name: 'False' },
            ]
          : [],
    },
    columnType: type,
    defaultUnit: getDefaultUnitBySpecificFilterFnKey(specificFilterFnKey),
    defaultSearchField,
  };
};

const mergeAdditionalFilterItems = (
  filterItems: FilterItem[],
  additional?: FilterItem[],
): FilterItem[] => {
  if (!additional) {
    return filterItems;
  }

  const result = [...filterItems];
  additional.forEach((addItem) => {
    const targetIndex = result.findIndex((item) => item.key.id === addItem.key.id);
    if (targetIndex === -1) {
      result.push(addItem);
    } else {
      result[targetIndex] = addItem;
    }
  });

  return result;
};

export const getFilterItemsByGridColumns = (
  columns: CustomColumn[] | Readonly<CustomColumn[]>,
  additional?: FilterItem[],
): FilterItem[] => {
  const filterItems: FilterItem[] = columns
    .filter(({ searchable }) => searchable)
    .map(({ caption, field, type, specificFilterFnKey, attrs, defaultSearchField }) =>
      createFilterItem({
        caption,
        field,
        type,
        specificFilterFnKey,
        attrs,
        defaultSearchField,
      }),
    );

  return mergeAdditionalFilterItems(filterItems, additional);
};

// ModernGrid 에서 사용하는 getFilterItemsByGridColumns
export const createTokenFilterItems = <RowDataType extends GridRawRowDataType>(
  columns: GridColumnDef<RowDataType>[],
  additional?: FilterItem[],
): FilterItem[] => {
  const processColumn = (
    column: GridColumnDef<RowDataType>,
    parentHeader?: string,
  ): FilterItem[] => {
    const { header, id, accessorKey, meta, columns: childColumns } = column;
    const { type = '', specificFilterFnKey, defaultSearchField, attrs } = meta ?? {};

    let name = '';
    if (typeof header === 'function') {
      name = header({} as HeaderContext<GridRowDataType<RowDataType>, unknown>) as string;
    } else if (typeof header === 'string') {
      name = header;
    } else if (!isUndefined(header)) {
      name = String(header);
    }

    if (childColumns?.length) {
      return childColumns.flatMap((child) => processColumn(child, name));
    }

    const caption = parentHeader ? `${parentHeader} ${name}` : name;

    return [
      createFilterItem({
        caption,
        field: id || accessorKey || '',
        type,
        specificFilterFnKey,
        defaultSearchField,
        attrs,
      }),
    ];
  };

  const filterItems: FilterItem[] = getCanFilterColumns(columns).flatMap((column) =>
    processColumn(column),
  );

  return mergeAdditionalFilterItems(filterItems, additional);
};

// 24.11.05 삭제 예정입니다. 사용하지 마세요. env.define에서 key 정의해서 쓰세요
export const getFormattedFilterSearchStorageKey = (
  componentName: string,
  index?: number,
): string => {
  return `filterSearch_${window.location.pathname}_${componentName}_${index ?? ''}`;
};

export const operatorMap = {
  '==': 'is',
  '!=': 'is not',
  like: 'like',
  'not like': 'not like',
  '>': 'above',
  '>=': 'more',
  '<': 'below',
  '<=': 'less',
};

export const transformOperator = (
  data: Omit<FilterSelectedValues, 'tokens'> & {
    tokens: {
      key: string;
      operator: string;
      value: string | number | boolean;
    }[];
  },
) => {
  return {
    logicalOperator: data.logicalOperator,
    tokens: data.tokens.map(({ key, operator, value }) => {
      return {
        key,
        operator: operatorMap[operator],
        value,
      };
    }),
  };
};

const parseValue = (
  token: {
    key: string;
    operator: string;
    value: string;
  },
  columnTypeMap: Record<string, { type: string; specificFilterFnKey?: string }>,
): {
  key: string;
  operator: string;
  value: string | number | boolean;
} => {
  const { type = 'string', specificFilterFnKey } = columnTypeMap[token.key];
  const rawValue = token.value;
  let parsedValue: string | number = rawValue;

  if (specificFilterFnKey) {
    parsedValue =
      FILTER_SEARCH_TOKEN_VALUE_CONVERTERS_BY_SPECIFIC_FILTER_FN[specificFilterFnKey](rawValue);
  } else if (type === 'number' || type === 'float' || type === 'stringNumber') {
    const numericValue = +rawValue;
    parsedValue = Number.isNaN(numericValue) ? rawValue : numericValue;
  } else if (type === 'datetime') {
    const utcZeroTime = standardTimeToUtcZeroTime(rawValue);
    parsedValue = utcZeroTime === 'Invalid Date' ? rawValue : utcZeroTime;
  }

  return {
    ...token,
    value: parsedValue,
  };
};

export const parseTokenFilter = (
  filterValue: FilterSelectedValues,
  columnTypeMap: Record<string, { type: string; specificFilterFnKey?: string }>,
): Omit<FilterSelectedValues, 'tokens'> & {
  tokens: {
    key: string;
    operator: string;
    value: string | number | boolean;
  }[];
} => ({
  logicalOperator: filterValue.logicalOperator,
  tokens: (filterValue.tokens ?? []).map((token) => parseValue(token, columnTypeMap)),
});

export const compareTimeValues = ({
  cellValue,
  searchValue,
  operator,
}: {
  cellValue: string | number | undefined;
  searchValue: string | string[] | number | undefined;
  operator: string;
}): Visible => {
  if (isNil(cellValue) || isNil(searchValue)) {
    return false;
  }
  const cellDate = dayjs(cellValue);

  if (!cellDate.isValid()) {
    return false;
  }

  // 'between' 연산자 처리
  if (operator === 'between') {
    if (Array.isArray(searchValue) && searchValue.length === 2) {
      const [startDate, endDate] = [dayjs(searchValue[0]), dayjs(searchValue[1])];

      if (startDate.isValid() && endDate.isValid()) {
        // isBetween은 isSameOrAfter/Before 조합을 사용
        return (
          cellDate.isSameOrAfter(startDate, 'second') && cellDate.isSameOrBefore(endDate, 'second')
        );
      }
    }
    return false;
  }

  // 4. '>=', '<=' 등 단일 날짜 비교 연산자 처리
  const searchDate = dayjs(searchValue as string | number);

  if (!searchDate.isValid()) {
    return false;
  }

  switch (operator) {
    case '>=':
      return cellDate.isSameOrAfter(searchDate, 'second');
    case '<=':
      return cellDate.isSameOrBefore(searchDate, 'second');
    case '>':
      return cellDate.isAfter(searchDate, 'second');
    case '<':
      return cellDate.isBefore(searchDate, 'second');
    case '==':
      return cellDate.isSame(searchDate, 'second');
    case '!=':
      return !cellDate.isSame(searchDate, 'second');
    default:
      return false;
  }
};
